import React, { createContext, useContext, useState, useEffect } from 'react';
import { CategoryInsightsSettingsContext } from 'contexts/CategoryInsightsSettingsContext';
import { CategoryManagementContext } from 'contexts/CategoryManagementContext';
import { getAverageSpendBySubCategory } from 'utils/apiRequests';
import {
	ColorSymbol,
	ColorWrapper,
	TooltipGrid,
	TooltipRowTitle,
	TooltipRowValue,
	TooltipWrapper,
} from './DashboardChart.styles';
import {
	chartheight,
	FONTSTYLE,
} from 'CategoryManagement/Dashboard/ChartsList/chartUtils';
import { renderToString } from 'react-dom/server';
import { getFormattedSpendWithSymbol } from 'utils/number';

const VendorSpendByCategory = createContext();

export const VendorSpendByCategoryProvider = ({
	children,
	currencySymbol,
	hideDecimals,
}) => {
	const [isLoading, setIsLoading] = useState(true);
	const [data, setData] = useState([]);
	const [options, setOptions] = useState({
		chart: {
			type: 'pie',
			height: chartheight,
		},
		title: { text: '' },
		exporting: { enabled: false, allowHTML: true },
		credits: { enabled: false },
		plotOptions: {
			pie: {
				allowPointSelect: true,
				cursor: 'pointer',
				dataLabels: {
					enabled: true,
					distance: -35,
					formatter: function() {
						return renderToString(
							<TooltipRowValue style={{ fontSize: '10px' }}>
								{getFormattedSpendWithSymbol(
									this.y,
									currencySymbol,
									hideDecimals
								).toUpperCase()}
							</TooltipRowValue>
						);
					},
				},
				showInLegend: true,
			},
		},
		legend: {
			align: 'right',
			verticalAlign: 'middle',
			layout: 'vertical',
			labelFormatter: function() {
				return this.name.split(' ')[0];
			},
			itemStyle: {
				...FONTSTYLE,
				textOverflow: 'ellipsis',
			},
		},
		tooltip: {
			enabled: true,
			shared: true,
			distance: 56,
			useHTML: true,
			backgroundColor: 'rgba(255,255,255, 0.9)',
			borderWidth: 0,
			outside: true,
			borderRadius: 2,
		},
		// series: [],
		colors: ['#032E45', '#0D3E57', '#448EB4', '#4FAEC6', '#5ABED9', '#65CEEB'],
	});

	const { currentSelectCategory, dateRangeData, isFetching } = useContext(
		CategoryManagementContext
	);
	const { loading: isSettingsFetching } = useContext(
		CategoryInsightsSettingsContext
	);
	const { categoryName } = currentSelectCategory;
	const { dateFrom, dateTo } = dateRangeData;

	useEffect(
		() => {
			if (!isSettingsFetching && !isFetching) {
				setIsLoading(true);
				if (
					categoryName !== '' ||
					categoryName !== null ||
					categoryName !== undefined
				) {
					const payload = {
						categoryName: categoryName,
						dateFrom: dateFrom,
						dateTo: dateTo,
					};
					getAverageSpendBySubCategory(payload)
						.then(response => {
							if (response.length > 0) {
								setData(response);
								const [xValue, yValue] = Object.keys(response[0]);
								const seriesResult = [
									{
										name: 'Data',
										data: response.map(item => ({
											name: item[xValue],
											y: item[yValue],
										})),
									},
								];
								setOptions(prevState => ({
									...prevState,
									series: seriesResult,
									tooltip: {
										...prevState.tooltip,
										formatter: function() {
											const { points } = this;
											return response.length > 1
												? renderToString(
														<TooltipWrapper>
															<TooltipGrid>
																<React.Fragment key={Math.random()}>
																	<ColorWrapper>
																		<ColorSymbol color={this.color} />
																	</ColorWrapper>
																	<TooltipRowTitle>
																		{this.point?.name}
																	</TooltipRowTitle>
																	<TooltipRowValue>
																		{getFormattedSpendWithSymbol(
																			this.y,
																			currencySymbol,
																			hideDecimals
																		).toUpperCase()}
																	</TooltipRowValue>
																</React.Fragment>
															</TooltipGrid>
														</TooltipWrapper>
												  )
												: renderToString(
														<TooltipWrapper>
															<TooltipGrid>
																<ColorWrapper>
																	<ColorSymbol color={points[0].color} />
																</ColorWrapper>
																<TooltipRowValue>
																	{getFormattedSpendWithSymbol(
																		points[0].y,
																		currencySymbol,
																		hideDecimals
																	).toUpperCase()}
																</TooltipRowValue>
															</TooltipGrid>
														</TooltipWrapper>
												  );
										},
									},
								}));
								setIsLoading(false);
							} else {
								setIsLoading(false);
							}
						})
						.catch(() => setIsLoading(false));
				} else {
					setIsLoading(false);
				}
			}
		}, // eslint-disable-next-line
		[dateRangeData, categoryName, isSettingsFetching, isFetching]
	);

	return (
		<VendorSpendByCategory.Provider value={{ isLoading, data, options }}>
			{children}
		</VendorSpendByCategory.Provider>
	);
};

export const useVendorSpendByCategoryContext = () => {
	return useContext(VendorSpendByCategory);
};
